import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ActivatedRoute, Router } from "@angular/router";
import type { Guid, ITrainingSessionInfo } from "@kiraah-shared/common";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { MobileVoicePurchaseRequestRecorderComponent } from "../../voice-purchase-request/mobile-voice-purchase-request-recorder/mobile-voice-purchase-request-recorder.component";
import { MobileAuthStore } from "../../state/mobile-auth.store";
import {
  MOBILE_TRAINING_SESSION_ROUTE_SEGMENTS,
  mobileTrainingSessionListUrl,
  mobileTrainingSessionAiQuestionnaireResultUrl,
} from "../../e-satisfaction-signature/mobile-training-session-route.provider";
import { createMobileAiQuestionnaireAccess } from "../helpers/mobile-ai-questionnaire-access";
import { MobileAiQuestionnaireClarifyChatComponent } from "../mobile-ai-questionnaire-clarify-chat/mobile-ai-questionnaire-clarify-chat.component";
import { MobileModuleAccessService } from "../../profile/services/mobile-module-access.service";
import {
  MobileAiQuestionnaireSessionStatus,
  type IMobileAiQuestionnaireClarifyMessage,
} from "../models/mobile-ai-questionnaire.models";
import { MobileAiQuestionnaireService } from "../services/mobile-ai-questionnaire.service";

type MobileAiQuestionnaireWorkflowPhase = "recorder" | "clarifying";

@Component({
  selector: "mobile-ai-questionnaire-workflow-page",
  standalone: true,
  imports: [CommonModule, TranslateModule, MobileVoicePurchaseRequestRecorderComponent, MobileAiQuestionnaireClarifyChatComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: "./mobile-ai-questionnaire-workflow-page.component.html",
  styleUrl: "./mobile-ai-questionnaire-workflow-page.component.scss",
})
export class MobileAiQuestionnaireWorkflowPageComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly moduleAccess = inject(MobileModuleAccessService);
  private readonly authStore = inject(MobileAuthStore);
  private readonly aiQuestionnaireService = inject(MobileAiQuestionnaireService);
  private readonly navSegments = inject(MOBILE_TRAINING_SESSION_ROUTE_SEGMENTS);

  private readonly aiQuestionnaireAccess = createMobileAiQuestionnaireAccess(this.moduleAccess);

  protected readonly phase = signal<MobileAiQuestionnaireWorkflowPhase>("recorder");
  protected readonly sessionUid = signal<Guid | null>(null);
  protected readonly trainingSessionUid = signal<Guid | null>(null);
  protected readonly continueTranscriptUid = signal<Guid | null>(null);
  protected readonly sessionLabel = signal<string | null>(null);
  protected readonly sessionHint = signal<ITrainingSessionInfo | null>(null);
  protected readonly errorMessageKey = signal<string | null>(null);
  protected readonly isSubmitting = signal(false);
  protected readonly clarifyMessages = signal<IMobileAiQuestionnaireClarifyMessage[]>([]);
  protected readonly requiresMoreClarification = signal(true);

  protected readonly hasAccess = computed(() => {
    const session = this.sessionHint();
    const identityUid = this.authStore.authResult()?.ProfilData?.Identity?.Uid;
    return this.aiQuestionnaireAccess.canUseVoiceActionForSession(session, identityUid);
  });

  ngOnInit(): void {
    const fromHistory = (history.state?.["trainingSessionHint"] ?? null) as ITrainingSessionInfo | null;
    if (fromHistory?.Uid) {
      this.sessionHint.set(fromHistory);
    }

    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      this.trainingSessionUid.set((params.get("sessionUid") as Guid) ?? null);
    });

    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((queryParams) => {
      const label = queryParams.get("sessionName");
      this.sessionLabel.set(label?.trim() || this.sessionHint()?.Name?.trim() || null);
      const transcriptUid = queryParams.get("transcriptUid");
      this.continueTranscriptUid.set((transcriptUid?.trim() as Guid) || null);
      this.resumeClarifyingSessionIfNeeded();
    });
  }

  protected onInitialSent(rawText: string): void {
    const trainingSessionUid = this.trainingSessionUid();
    const text = rawText.trim();
    if (!trainingSessionUid || !text || this.isSubmitting() || !this.hasAccess()) {
      return;
    }

    this.isSubmitting.set(true);
    this.errorMessageKey.set(null);

    this.aiQuestionnaireService
      .submitTranscript(trainingSessionUid, text, this.continueTranscriptUid())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (transcript) => {
          this.sessionUid.set(transcript.Uid);
          this.clarifyMessages.set([{ role: "user", text }]);
          this.runClarify(null);
        },
        error: (err: Error) => this.handleError(err, "mobile.aiQuestionnaire.error.submitFailed"),
      });
  }

  protected onClarifyAnswer(answer: string): void {
    const text = answer.trim();
    if (!text || this.isSubmitting() || !this.sessionUid()) {
      return;
    }
    this.clarifyMessages.update((messages) => [...messages, { role: "user", text }]);
    this.runClarify(text);
  }

  protected onGenerateDraft(): void {
    const sessionUid = this.sessionUid();
    const trainingSessionUid = this.trainingSessionUid();
    if (!sessionUid || !trainingSessionUid || this.isSubmitting()) {
      return;
    }

    this.isSubmitting.set(true);
    this.errorMessageKey.set(null);

    this.aiQuestionnaireService
      .generateQuiz(sessionUid)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.isSubmitting.set(false);
          void this.router.navigateByUrl(
            mobileTrainingSessionAiQuestionnaireResultUrl(
              this.navSegments,
              String(trainingSessionUid),
              String(sessionUid),
              { sessionName: this.sessionLabel() ?? undefined },
            ),
            { state: { trainingSessionHint: this.sessionHint() } },
          );
        },
        error: (err: Error) => this.handleError(err, "mobile.aiQuestionnaire.error.generateFailed"),
      });
  }

  protected onRecordError(key: string): void {
    this.errorMessageKey.set(key);
  }

  protected backToList(): void {
    void this.router.navigateByUrl(mobileTrainingSessionListUrl(this.navSegments));
  }

  protected errorMessage(): string {
    const key = this.errorMessageKey();
    if (!key) {
      return "";
    }
    return this.translate.instant(key);
  }

  protected clearError(): void {
    this.errorMessageKey.set(null);
  }

  private resumeClarifyingSessionIfNeeded(): void {
    const transcriptUid = this.continueTranscriptUid();
    if (!transcriptUid || this.sessionUid()) {
      return;
    }

    this.isSubmitting.set(true);
    this.aiQuestionnaireService
      .getSession(transcriptUid)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (session) => {
          if (session.Status !== MobileAiQuestionnaireSessionStatus.Clarifying) {
            this.isSubmitting.set(false);
            return;
          }

          this.sessionUid.set(session.Uid);
          this.clarifyMessages.set(this.buildMessagesFromRawText(session.RawText));
          this.phase.set("clarifying");
          this.runClarify(null);
        },
        error: () => {
          this.isSubmitting.set(false);
        },
      });
  }

  private runClarify(userAnswer: string | null): void {
    const sessionUid = this.sessionUid();
    if (!sessionUid) {
      this.isSubmitting.set(false);
      return;
    }

    this.isSubmitting.set(true);
    this.phase.set("clarifying");
    this.errorMessageKey.set(null);

    this.aiQuestionnaireService
      .clarify(sessionUid, userAnswer)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          this.isSubmitting.set(false);
          this.clarifyMessages.update((messages) => {
            const last = messages[messages.length - 1];
            if (last?.role === "assistant" && last.text === result.AssistantMessage) {
              return messages;
            }
            return [
              ...messages,
              {
                role: "assistant",
                text: result.AssistantMessage,
                subjects: result.AvailableSubjects?.length ? result.AvailableSubjects : undefined,
              },
            ];
          });
          this.requiresMoreClarification.set(result.RequiresMoreClarification);
        },
        error: (err: Error) => this.handleError(err, "mobile.aiQuestionnaire.error.clarifyFailed"),
      });
  }

  private buildMessagesFromRawText(rawText: string): IMobileAiQuestionnaireClarifyMessage[] {
    return rawText
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter((line) => line.length > 0)
      .map((line) => ({ role: "user" as const, text: line }));
  }

  private handleError(err: Error, fallbackKey: string): void {
    this.isSubmitting.set(false);
    const key = err.message?.startsWith("mobile.")
      ? err.message
      : err.message?.startsWith("app.")
        ? fallbackKey
        : fallbackKey;
    this.errorMessageKey.set(key);
  }
}
